import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usePageSearch, matchesSearch, noMatchText } from '../lib/pageSearch';
import { getCEActivity, getCEAgentHealth, getCEJobScreenshots, fetchCEScreenshotUrl, getProjects } from '../api';

/* ──────────────────────────────────────────────────────────────────
 * CE Activity Page — role-scoped view of CE Agent's automation job
 * history.
 *
 * Scope (computed server-side; client just renders what comes back):
 *   PM (rank 1)            → own jobs only
 *   CM / CD (rank 2-3)     → self + everyone in reports_to subtree
 *   VP / Admin (rank 4-5)  → every job
 *
 * Columns: Job · Type · Status · Project · Requested by · Started ·
 * Duration · Message preview.
 *
 * Click a row to expand a drawer with the full message/error and any
 * replay-lineage links (read-only — historical context only). Replay
 * functionality is intentionally NOT exposed to end users; if a job
 * needs to be re-run, it's an operator action handled from the CE
 * Agent's own /dashboard/history page, not from Summit.
 *
 * 2026-05-28: warm-cream + sage token redesign to match the rest of
 * the chrome (Dashboard / Schedule / SOV / Billing / Rollup /
 * Performance). Drops `useTheme` + hardcoded hex; status badges use
 * --success / --warning / --danger / --accent semantics with 22%-mix
 * soft rings.
 * ────────────────────────────────────────────────────────────────── */

/* ─────────────────────── Status tokens ─────────────────────── */

const STATUS_META = {
  queued:         { label: 'Queued',    tone: 'neutral' },
  running:        { label: 'Running',   tone: 'accent'  },
  parked_waiting: { label: 'Parked',    tone: 'warning' },
  completed:      { label: 'Completed', tone: 'success' },
  partial:        { label: 'Partial',   tone: 'warning' },
  failed:         { label: 'Failed',    tone: 'danger'  },
};

const SCOPE_LABEL = {
  self: 'Showing your jobs',
  team: 'Showing your team',
  all:  'Showing all jobs',
};

/* ─────────────────────── Helpers ─────────────────────── */

// '14:32 May 27' style — compact + readable, no year for current year.
const fmtTimestamp = (d) => {
  if (!d) return '–';
  try {
    const dt = new Date(d);
    const sameYear = dt.getFullYear() === new Date().getFullYear();
    return dt.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }),
    }) + ' ' + dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return d; }
};

// Compact duration: 1.3s / 47s / 2m 14s / 1h 5m.
const fmtDuration = (s) => {
  if (s == null) return '–';
  const sec = Number(s);
  if (!Number.isFinite(sec)) return '–';
  if (sec < 10) return sec.toFixed(1) + 's';
  if (sec < 60) return Math.round(sec) + 's';
  if (sec < 3600) {
    const m = Math.floor(sec / 60);
    const ss = Math.round(sec % 60);
    return `${m}m ${ss}s`;
  }
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m`;
};

// 'JobType.CREATE_SOV' / 'create_sov' / 'CREATE_SOV' → 'CREATE_SOV'
const cleanJobType = (s) => String(s || '').replace(/^JobType\./, '').toUpperCase();
// Same idea for status: 'JobStatus.COMPLETED' → 'completed'
const cleanStatus = (s) => String(s || '').replace(/^JobStatus\./, '').toLowerCase();

/* ─────────────────────── Inline icons ─────────────────────── */

const RefreshIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const ChevronDown = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const CheckIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const XIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const ClockIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const GearIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);
const PauseIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="4" width="4" height="16" />
    <rect x="14" y="4" width="4" height="16" />
  </svg>
);
const HalfCircleIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2 A10 10 0 0 1 12 22 Z" />
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);
const Spinner = ({ size = 12, color = 'currentColor' }) => (
  <span style={{
    display: 'inline-block', width: size, height: size, borderRadius: 99,
    border: `2px solid color-mix(in oklch, ${color} 30%, transparent)`,
    borderTopColor: color,
    animation: 'spin .8s linear infinite',
    flexShrink: 0,
  }} />
);

const STATUS_ICONS = {
  queued:         ClockIcon,
  running:        GearIcon,
  parked_waiting: PauseIcon,
  completed:      CheckIcon,
  partial:        HalfCircleIcon,
  failed:         XIcon,
};

/* ─────────────────────── Status badge ─────────────────────── */

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.queued;
  const Icon = STATUS_ICONS[status] || ClockIcon;
  const palette = {
    success: { fg: 'var(--success)',    bg: 'var(--success-soft)' },
    warning: { fg: 'var(--warning)',    bg: 'var(--warning-soft)' },
    danger:  { fg: 'var(--danger)',     bg: 'var(--danger-soft)'  },
    accent:  { fg: 'var(--accent-text)', bg: 'var(--accent-soft)' },
    neutral: { fg: 'var(--text-3)',     bg: 'var(--bg-surface-2)' },
  }[meta.tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 9px 2px 8px', borderRadius: 999,
      fontSize: 12.5, fontWeight: 500,
      color: palette.fg, background: palette.bg,
      border: `1px solid color-mix(in oklch, ${palette.fg} 22%, transparent)`,
      letterSpacing: '-0.005em', whiteSpace: 'nowrap',
    }}>
      <span aria-hidden style={{ display: 'inline-flex' }}><Icon /></span>
      {meta.label}
    </span>
  );
};

/* ─────────────────────── Signature badge ─────────────────────── */

// dropbox_sign_status (set by CE-Agent on send, updated by Summit's
// Dropbox Sign webhook receiver) → label + tone. Values match the enum
// written by sync_db.set_dropbox_sign_envelope + routes/dropboxSignWebhook.
const SIGNATURE_META = {
  sent:             { label: 'Sent',     tone: 'neutral' },
  viewed:           { label: 'Viewed',   tone: 'accent'  },
  partially_signed: { label: 'Signing',  tone: 'warning' },
  completed:        { label: 'Signed',   tone: 'success' },
  declined:         { label: 'Declined', tone: 'danger'  },
  canceled:         { label: 'Canceled', tone: 'neutral' },
  expired:          { label: 'Expired',  tone: 'danger'  },
};

const TONE_PALETTE = {
  success: { fg: 'var(--success)',     bg: 'var(--success-soft)'  },
  warning: { fg: 'var(--warning)',     bg: 'var(--warning-soft)'  },
  danger:  { fg: 'var(--danger)',      bg: 'var(--danger-soft)'   },
  accent:  { fg: 'var(--accent-text)', bg: 'var(--accent-soft)'   },
  neutral: { fg: 'var(--text-3)',      bg: 'var(--bg-surface-2)'  },
};

// Renders the AFP signature-envelope state. A job with no envelope
// (dropbox_sign_status null) renders an em-dash — most jobs aren't
// CREATE_SOV and never fire an envelope. test_mode envelopes get a
// small TEST chip so a real signed AFP is never confused with a test.
const SignatureBadge = ({ status, testMode }) => {
  const s = String(status || '').toLowerCase();
  const meta = SIGNATURE_META[s];
  if (!meta) return <span style={{ color: 'var(--text-4)', fontSize: 13.5 }}>–</span>;
  const palette = TONE_PALETTE[meta.tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 8px', borderRadius: 999,
      fontSize: 12.5, fontWeight: 500,
      color: palette.fg, background: palette.bg,
      border: `1px solid color-mix(in oklch, ${palette.fg} 22%, transparent)`,
      letterSpacing: '-0.005em', whiteSpace: 'nowrap',
    }}>
      {meta.label}
      {testMode ? (
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
          padding: '1px 4px', borderRadius: 4,
          color: 'var(--text-3)', background: 'var(--bg-surface-2)',
          border: '1px solid var(--border-subtle)',
        }}>TEST</span>
      ) : null}
    </span>
  );
};

/* ─────────────────────── Shared input styles ─────────────────────── */

const selectStyle = {
  height: 32, padding: '0 28px 0 10px',
  fontSize: 14, color: 'var(--text-1)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  borderRadius: 8,
  outline: 'none', cursor: 'pointer',
  appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
  backgroundImage: 'none',
};
const inputStyle = {
  height: 32, padding: '0 10px',
  fontSize: 14, color: 'var(--text-1)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  borderRadius: 8,
  outline: 'none', boxSizing: 'border-box',
};
const onFocusRing = (e) => {
  e.currentTarget.style.borderColor = 'var(--accent)';
  e.currentTarget.style.boxShadow = '0 0 0 2px var(--accent-ring)';
};
const onBlurRing = (e) => {
  e.currentTarget.style.borderColor = 'var(--border-default)';
  e.currentTarget.style.boxShadow = 'none';
};

/* ─────────────────────── Filter select with caret ─────────────────────── */

const FilterSelect = ({ value, onChange, children, minWidth = 140 }) => (
  <div style={{ position: 'relative', display: 'inline-block', minWidth }}>
    <select
      value={value}
      onChange={onChange}
      style={{ ...selectStyle, width: '100%' }}
      onFocus={onFocusRing}
      onBlur={onBlurRing}
    >
      {children}
    </select>
    <span style={{
      position: 'absolute',
      right: 10, top: '50%',
      transform: 'translateY(-50%)',
      color: 'var(--text-3)',
      pointerEvents: 'none',
      display: 'inline-flex',
    }}>
      <ChevronDown />
    </span>
  </div>
);

/* ─────────────────────── Grid columns ─────────────────────── */

// Project is wider for the job name; Job / Requested by / Started give back the room they
// didn't use, so Message stays on screen at laptop width (1512px, sidebar open).
const COL_TEMPLATE = '100px 150px 105px 115px 160px 140px 115px 75px 1fr';
// Second line in a cell: the report a Q Tool pull ran (Type) and the job's name (Project).
const SUB_LINE = {
  fontSize: 12.5, color: 'var(--text-3)', marginTop: 2,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

// Which of the CE agents ran the job (2026-10-18: the brain + CE2 + CE3) -- under its status, so the
// Message column keeps its room. A job that never started has none.
const ranOnLabel = (name) => {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return null;
  return n === 'brain' ? 'on the brain' : `on ${n.toUpperCase()}`;
};

/* ─────────────────────── Main page ─────────────────────── */

/* The CE Agent is a separate process on a VM, so "unreachable" is a routine state
   rather than an exception -- the VM reboots, or the agent was never started. Its own
   message is right for a log and wrong for a banner: "CE Agent request timed out after
   15000ms" tells a user nothing they can act on, and it hides the actual meaning.
   Note what a FULL-LENGTH timeout implies: a reachable-but-closed port refuses
   immediately, so 15s of silence means packets were dropped -- i.e. nothing is
   listening on 8443. "The agent probably is not running" is the honest reading, and it
   is the one that points somebody at the fix. The raw text stays in console.error and
   in the banner's title attribute so it is still one hover away. */
const friendlyCEError = (raw) => {
  const m = String(raw || '');
  if (/timed out after/i.test(m)) return 'Couldn\u2019t reach the CE Agent \u2014 it may not be running on the VM.';
  if (/ECONNREFUSED|refused/i.test(m)) return 'The CE Agent refused the connection \u2014 it may be restarting.';
  if (/\b50[23]\b|Bad Gateway|Service Unavailable/i.test(m)) return 'The CE Agent is unavailable right now \u2014 it may be restarting.';
  if (/\b40[13]\b|signature|hmac/i.test(m)) return 'Summit could not authenticate to the CE Agent \u2014 the shared secret needs a look.';
  return m || 'Failed to fetch CE Activity';
};

/* ───────────────── Agent health strip ─────────────────
 * Surfaces the heartbeat monitor's PERSISTED verdict (GET
 * /api/ce-activity/health). That endpoint has existed since the monitor
 * shipped and had no consumer, which is how a 2026-09-02 routing problem on
 * one laptop got mistaken for a dead agent and "fixed" with a VM redeploy
 * that took a healthy agent down for 11 minutes.
 *
 * ⚠️ THE WHOLE POINT IS THE DISTINCTION IT DRAWS. Summit probes the agent
 * from INSIDE the VNet, so a green strip means "the agent is fine and your
 * own network path is the problem" -- which is why the copy says "reachable
 * from Summit" rather than just "healthy", and why the tooltip names the
 * route check. A red strip means the agent is unreachable from inside the
 * VNet too, and no amount of laptop networking will help.
 *
 * ⚠️ A FAILED FETCH IS "UNKNOWN", NEVER "DOWN". If we cannot read the
 * verdict we know nothing about the agent, and rendering that as an outage
 * would invent the exact false alarm this strip exists to prevent.
 *
 * It never probes the agent -- this is a read of stored state -- so polling
 * it costs the agent nothing and answers instantly while the agent is down.
 * ──────────────────────────────────────────────────────── */

const AGENT_HEALTH_POLL_MS = 30000;

// '41s' / '4m' / '2h 10m' — compact, for a strip that must stay one line.
const fmtAgo = (iso) => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const s = Math.round(ms / 1000);
  if (s < 90) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const fmtClock = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return null; }
};

export const AgentHealthStrip = ({ health, error }) => {
  // Order matters: an unreadable verdict is UNKNOWN, and only then do we
  // trust `state`. failStreak > 0 while still 'up' is the early-warning
  // window -- worth showing, because it is the difference between "wait,
  // the VM watchdog fixes most of these" and "go look".
  // 'loading' is NOT 'unknown'. Before the first fetch resolves we know
  // nothing yet, and painting the alarming unknown state for that instant
  // would flash a scary line on every page load.
  // A failed read while we still hold a PREVIOUS verdict keeps that verdict
  // and marks it stale -- the last known answer beats no answer, as long as
  // the UI does not pretend it is current.
  const stale = Boolean(error && health);
  let tone;
  if (!health && !error) tone = 'loading';
  else if (!health) tone = 'unknown';
  else if (health.state === 'down') tone = 'danger';
  else if ((health.failStreak || 0) > 0) tone = 'warning';
  else tone = 'success';

  const COLOR = {
    success: 'var(--success)',
    warning: 'var(--warning)',
    danger:  'var(--danger)',
    unknown: 'var(--text-4)',
    loading: 'var(--text-4)',
  }[tone];

  const loud = tone === 'danger';

  let headline;
  let detail = null;
  let chip = null;
  let tip;

  if (tone === 'loading') {
    headline = 'Checking agent status…';
    tip = undefined;
  } else if (tone === 'unknown') {
    headline = 'Agent health unavailable';
    detail = 'Summit could not read the heartbeat monitor’s verdict. That says nothing '
      + 'about the agent itself — it may be running normally.';
    tip = error || 'Could not reach /api/ce-activity/health';
  } else if (tone === 'danger') {
    const mins = health.downSince
      ? Math.max(1, Math.round((Date.now() - new Date(health.downSince).getTime()) / 60000))
      : null;
    headline = mins
      ? `Agent unreachable for ${mins} minute${mins === 1 ? '' : 's'}`
      : 'Agent unreachable';
    const since = fmtClock(health.downSince);
    detail = `${since ? `Unreachable since ${since}. ` : ''}Summit has missed `
      + `${health.failStreak} consecutive checks from inside the VNet, which is past the VM `
      + `watchdog’s 5-minute repair cycle — it has not fixed itself. CE Activity, AR `
      + `pulls and job-cost pulls will fail until it is back.`;
    // announced reflects a mail that actually left, not merely an attempt.
    if (health.announced) chip = { text: 'alert sent', tone: 'danger' };
    else if (health.alertTries > 0) chip = { text: `alert not delivered (${health.alertTries} tries)`, tone: 'warning' };
    tip = health.lastError || undefined;
  } else if (tone === 'warning') {
    headline = `Agent not responding — ${health.failStreak} of ${health.failThreshold} checks missed`;
    detail = `No alert yet. The VM watchdog re-checks every 5 minutes and usually recovers the `
      + `agent on its own, so Summit waits for ${health.failThreshold} misses `
      + `(~${health.minutesToAlert} min) before mailing.`;
    tip = health.lastError || undefined;
  } else {
    headline = 'Agent reachable from Summit';
    tip = 'Summit probes the agent from inside the VNet every '
      + `${health.probeIntervalSeconds}s. If this is green and you still cannot load the agent’s `
      + 'own dashboard, the agent is fine and your network route is the problem — check '
      + '"route -n get 10.0.1.4"; a utun (VPN) interface there means a VPN is swallowing it.';
  }

  const ago = tone === 'success' || tone === 'warning' ? fmtAgo(health && health.lastOkAt) : null;

  // The agent can be perfectly REACHABLE and still unable to raise an alarm --
  // an expired Entra secret, blocked egress, Graph throttling. That is a second,
  // independent verdict, so it gets its own row rather than being folded into
  // the reachability line where it would be mistaken for an outage.
  // Only shown while the agent is reachable: evaluateMailHealth runs solely on a
  // successful probe, so during an outage this value is stale by construction
  // and the outage itself is the thing worth saying.
  const reachable = tone === 'success' || tone === 'warning';
  const mailBad = reachable && health && health.mailState === 'bad';
  // UNKNOWN is not OK. mailState defaults to 'ok', so without mailReported the
  // UI would claim a healthy channel on a build that never sends the field --
  // which is exactly what was true for 10 days while this watch sat inert.
  const mailUnknown = tone === 'success' && health && health.mailReported === false;

  const primary = (
    <div
      title={tip}
      style={{
        display: 'flex', alignItems: loud ? 'flex-start' : 'center', gap: 9,
        padding: loud ? '10px 12px' : '7px 11px',
        borderRadius: 8,
        fontSize: 14,
        background: loud
          ? 'color-mix(in oklch, var(--danger) 6%, var(--bg-surface))'
          : 'var(--bg-surface)',
        border: `1px solid ${loud
          ? 'color-mix(in oklch, var(--danger) 28%, var(--border-default))'
          : 'var(--border-default)'}`,
        boxShadow: 'var(--shadow-xs)',
        cursor: tip ? 'help' : 'default',
      }}
    >
      <span style={{
        width: loud ? 7 : 6, height: loud ? 7 : 6, borderRadius: '50%',
        background: COLOR, flexShrink: 0, marginTop: loud ? 5 : 0,
      }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            color: (tone === 'unknown' || tone === 'loading') ? 'var(--text-3)' : COLOR,
            fontWeight: loud ? 500 : 400,
          }}>{headline}</span>
          {ago && (
            <>
              <span style={{ color: 'var(--text-4)' }}>·</span>
              <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)' }}>
                last seen {ago} ago
              </span>
            </>
          )}
          {tone === 'success' && health.okStreak > 1 && (
            <>
              <span style={{ color: 'var(--text-4)' }}>·</span>
              <span style={{ color: 'var(--text-3)' }}>{health.okStreak} clean checks</span>
            </>
          )}
          {mailUnknown && (
            <>
              <span style={{ color: 'var(--text-4)' }}>·</span>
              <span style={{ color: 'var(--text-3)' }} title={'The agent did not report its mail-channel health, so Summit cannot tell whether alerts can be sent. Usually means the VM is running a build older than the one that reports it -- git pull on the VM and bounce the agent.'}>
                mail status not reported
              </span>
            </>
          )}
          {stale && (
            <>
              <span style={{ color: 'var(--text-4)' }}>·</span>
              <span style={{ color: 'var(--warning)' }}>verdict may be stale</span>
            </>
          )}
          {chip && (
            <span style={{
              marginLeft: 'auto', flexShrink: 0,
              padding: '1px 7px', borderRadius: 5, fontSize: 13,
              background: `color-mix(in oklch, var(--${chip.tone}) 12%, var(--bg-surface))`,
              color: `var(--${chip.tone})`,
            }}>{chip.text}</span>
          )}
        </div>
        {detail && (
          <div style={{ marginTop: 5, fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
            {detail}
          </div>
        )}
      </div>
    </div>
  );

  if (!mailBad) return primary;

  // Deliberately loud and separate. "The agent is running and cannot tell you
  // when something breaks" is worse than a plain outage, because every alert
  // raised on the VM -- job failures, a blocking dialog on the CE desktop, CE
  // hung, the digest -- is being dropped silently, and the agent cannot report
  // this about itself. It only reaches you because Summit's mail path is
  // independent of the agent's.
  const mailMins = health.mailBadSince
    ? Math.max(1, Math.round((Date.now() - new Date(health.mailBadSince).getTime()) / 60000))
    : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {primary}
      <div
        title={health.mailLastError || undefined}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 9,
          padding: '10px 12px', borderRadius: 8, fontSize: 14,
          background: 'color-mix(in oklch, var(--danger) 6%, var(--bg-surface))',
          border: '1px solid color-mix(in oklch, var(--danger) 28%, var(--border-default))',
          boxShadow: 'var(--shadow-xs)',
          cursor: health.mailLastError ? 'help' : 'default',
        }}
      >
        <span style={{
          width: 7, height: 7, borderRadius: '50%',
          background: 'var(--danger)', flexShrink: 0, marginTop: 5,
        }} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--danger)', fontWeight: 500 }}>
              Agent cannot send mail{mailMins ? ` — ${mailMins} minute${mailMins === 1 ? '' : 's'}` : ''}
            </span>
            {health.mailAnnounced && (
              <span style={{
                marginLeft: 'auto', flexShrink: 0,
                padding: '1px 7px', borderRadius: 5, fontSize: 13,
                background: 'color-mix(in oklch, var(--danger) 12%, var(--bg-surface))',
                color: 'var(--danger)',
              }}>alert sent</span>
            )}
          </div>
          <div style={{ marginTop: 5, fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
            The agent is running normally but its mail channel is broken, so every alert it
            raises on the VM — job failures, a blocking dialog on the CE desktop, CE hung, the
            digest — is being lost silently. Most likely the Entra client secret expired.
            {health.mailLastError ? ' Hover for the reported error.' : ''}
          </div>
        </div>
      </div>
    </div>
  );
};

const CEActivityPage = ({ onOpenProject: openBySlug }) => {
  const [data, setData] = useState({ jobs: [], total: 0, scope: 'self' });
  // A CE job's project_id is its CE job NUMBER (e.g. STOCI-D-01), not a Summit project id or
  // slug, so opening it directly landed on "couldn't find that project". Map the job number to
  // the project's slug + name the way the server does (ceBilling.ceJobOf: ce_job_id, else
  // project_number); a job with no Summit project you can see stays plain text, unnamed.
  const [projByJob, setProjByJob] = useState({});
  useEffect(() => {
    getProjects().then((list) => {
      const m = {};
      (list || []).forEach((p) => {
        const k = String(p.ce_job_id || p.project_number || '').trim().toUpperCase();
        if (k && p.slug) m[k] = { slug: p.slug, name: p.project_name || '' };
      });
      setProjByJob(m);
    }).catch(() => {});
  }, []);
  const projFor = (jobNumber) => projByJob[String(jobNumber || '').trim().toUpperCase()] || null;
  const slugFor = (jobNumber) => projFor(jobNumber)?.slug || null;
  const onOpenProject = openBySlug
    ? (jobNumber) => { const slug = slugFor(jobNumber); if (slug) openBySlug(slug); }
    : null;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  // Agent health is INDEPENDENT of the job-history fetch on purpose. If the
  // history call fails the health verdict is still the useful answer, and if
  // the health call fails the history must still render.
  const [agentHealth, setAgentHealth] = useState(null);
  const [agentHealthError, setAgentHealthError] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterProject, setFilterProject] = useState('');
  // The top bar's search (lib/pageSearch) narrows the rows these filters loaded: job, type,
  // report, status, signature, project number + name, requested by, message.
  const search = usePageSearch('Search CE activity…');

  // Inject spin keyframe once for the Spinner.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const id = 'ce-activity-anim';
    if (document.getElementById(id)) return undefined;
    const s = document.createElement('style');
    s.id = id;
    s.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(s);
    return () => {};
  }, []);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (silent) setRefreshing(true); else setLoading(true);
      setError(null);
      const params = { limit: 100 };
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.job_type = filterType;
      if (filterProject) params.project_id = filterProject;
      const result = await getCEActivity(params);
      setData(result);
    } catch (e) {
      console.error('CE Activity fetch failed:', e);
      setError({ text: friendlyCEError(e.message), raw: e.message || '' });
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  }, [filterStatus, filterType, filterProject]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reads stored state only -- no probe reaches the agent -- so a 30s poll is
  // free. Keeps the previous verdict on a transient failure rather than
  // blanking the strip, but clears the error once a read succeeds again.
  const fetchAgentHealth = useCallback(async () => {
    try {
      const h = await getCEAgentHealth();
      setAgentHealth(h);
      setAgentHealthError(null);
    } catch (e) {
      console.error('Agent health fetch failed:', e);
      setAgentHealthError(e.message || 'unavailable');
    }
  }, []);

  useEffect(() => {
    let alive = true;
    const tick = () => { if (alive) fetchAgentHealth(); };
    tick();
    const id = setInterval(tick, AGENT_HEALTH_POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [fetchAgentHealth]);

  const shownJobs = data.jobs.filter((j) => matchesSearch(search, j.job_id, cleanJobType(j.job_type), j.detail,
    (STATUS_META[cleanStatus(j.status)] || STATUS_META.queued).label,
    SIGNATURE_META[String(j.dropbox_sign_status || '').toLowerCase()]?.label,
    j.project_id, projFor(j.project_id)?.name, j.requested_by, j.error_message || j.message));

  return (
    <div style={{
      padding: '20px 24px 24px',
      display: 'flex', flexDirection: 'column', gap: 16,
      maxWidth: 1600, margin: '0 auto',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h1 style={{
            margin: 0, fontSize: 23.5, fontWeight: 600,
            color: 'var(--text-1)', letterSpacing: '-0.02em',
          }}>CE Activity</h1>
          <div style={{ fontSize: 14, color: 'var(--text-3)', marginTop: 3 }}>
            {SCOPE_LABEL[data.scope] || 'Job history from the ComputerEase automation agent.'}
            {data.total > 0 && (
              <>
                {' · '}
                <span className="mono" style={{ letterSpacing: '-0.02em' }}>
                  {data.total}
                </span>{' '}
                job{data.total === 1 ? '' : 's'} total
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => { fetchData({ silent: true }); fetchAgentHealth(); }}
          disabled={refreshing}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            height: 32, padding: '0 12px', borderRadius: 8,
            fontSize: 14, fontWeight: 500,
            color: 'var(--text-2)',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            boxShadow: 'var(--shadow-xs)',
            cursor: refreshing ? 'wait' : 'pointer',
            opacity: refreshing ? 0.85 : 1,
            flexShrink: 0,
          }}
          onMouseEnter={(e) => { if (!refreshing) e.currentTarget.style.background = 'var(--bg-hover)'; }}
          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-surface)'}
        >
          {refreshing ? <Spinner size={11} /> : <RefreshIcon />}
          Refresh
        </button>
      </div>

      {/* Agent health — quiet when reachable, loud when not. */}
      <AgentHealthStrip health={agentHealth} error={agentHealthError} />

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <FilterSelect
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          minWidth={150}
        >
          <option value="">All statuses</option>
          <option value="completed">Completed</option>
          <option value="partial">Partial</option>
          <option value="failed">Failed</option>
          <option value="running">Running</option>
          <option value="queued">Queued</option>
          <option value="parked_waiting">Parked</option>
        </FilterSelect>
        <FilterSelect
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          minWidth={170}
        >
          <option value="">All job types</option>
          <option value="CREATE_NEW_JOB">Create New Job</option>
          <option value="CREATE_SOV">Create SOV</option>
          <option value="UPDATE_SOV_PROGRESS">Update SOV Progress</option>
          <option value="PRINT_PAY_APP">Print Pay App</option>
          <option value="RUN_QTOOL_REPORT">Q Tool Report</option>
        </FilterSelect>
        <input
          type="text"
          placeholder="Filter by project ID…"
          value={filterProject}
          onChange={(e) => setFilterProject(e.target.value)}
          style={{ ...inputStyle, minWidth: 200, flex: '0 0 auto' }}
          onFocus={onFocusRing}
          onBlur={onBlurRing}
        />
      </div>

      {/* Table / loading / empty / error */}
      {loading ? (
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 12,
          boxShadow: 'var(--shadow-sm)',
          padding: '60px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
          color: 'var(--text-3)', fontSize: 14.5,
        }}>
          <Spinner size={18} color="var(--accent)" /> Loading CE Activity…
        </div>
      ) : error ? (
        <div title={error.raw || undefined} style={{
          margin: 0, padding: '12px 14px 12px 16px',
          background: 'var(--danger-soft)',
          borderRadius: 8,
          boxShadow: 'inset 3px 0 0 var(--danger)',
          border: '1px solid color-mix(in oklch, var(--danger) 18%, transparent)',
          color: 'var(--danger)', fontSize: 14,
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <span style={{ flex: 1, minWidth: 200 }}>{error.text}</span>
          <button
            type="button"
            onClick={() => fetchData()}
            style={{ flex: '0 0 auto', height: 28, padding: '0 12px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--danger)', background: 'var(--bg-surface)', border: '1px solid color-mix(in oklch, var(--danger) 35%, transparent)' }}
          >
            Try again
          </button>
        </div>
      ) : shownJobs.length === 0 ? (
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 12,
          boxShadow: 'var(--shadow-sm)',
          padding: '48px 24px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 15.5, color: 'var(--text-2)', marginBottom: 4 }}>
            {search.trim() && data.jobs.length > 0 ? noMatchText(search).body : 'No CE Agent jobs match these filters.'}
          </div>
          {data.scope === 'self' && (
            <div style={{ fontSize: 13.5, color: 'var(--text-4)' }}>
              You only see your own jobs as a Project Manager.
            </div>
          )}
        </div>
      ) : (
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 12,
          boxShadow: 'var(--shadow-sm)',
          overflow: 'hidden',
        }}>
          <div style={{ maxHeight: 'calc(100vh - 280px)', minHeight: 240, overflow: 'auto' }}>
            {/* Sticky header */}
            <div style={{
              display: 'grid', gridTemplateColumns: COL_TEMPLATE, columnGap: 12,
              height: 38, alignItems: 'center',
              padding: '0 16px',
              position: 'sticky', top: 0, zIndex: 3,
              background: 'var(--bg-surface-2)',
              borderBottom: '1px solid var(--border-subtle)',
            }}>
              {['Job', 'Type', 'Status', 'Signature', 'Project', 'Requested by',
                'Started', 'Duration', 'Message'].map((label) => (
                <div key={label} style={{
                  fontSize: 12, fontWeight: 600,
                  color: 'var(--text-3)',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  whiteSpace: 'nowrap',
                }}>{label}</div>
              ))}
            </div>

            {shownJobs.map((j, idx) => {
              const status = cleanStatus(j.status);
              const isOpen = expandedId === j.job_id;
              const isLast = idx === shownJobs.length - 1;
              const proj = projFor(j.project_id);
              return (
                <React.Fragment key={j.job_id}>
                  <div
                    onClick={() => setExpandedId(isOpen ? null : j.job_id)}
                    style={{
                      display: 'grid', gridTemplateColumns: COL_TEMPLATE, columnGap: 12,
                      minHeight: 56, alignItems: 'center',
                      padding: '0 16px',
                      borderBottom: (!isLast || isOpen) ? '1px solid var(--border-subtle)' : 'none',
                      background: isOpen ? 'var(--bg-hover)' : 'var(--bg-surface)',
                      cursor: 'pointer',
                      transition: 'background .12s',
                    }}
                    onMouseEnter={(e) => { if (!isOpen) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={(e) => { if (!isOpen) e.currentTarget.style.background = 'var(--bg-surface)'; }}
                  >
                    <div className="mono" style={{
                      fontSize: 12.5, color: 'var(--text-3)',
                      letterSpacing: '-0.02em',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={j.job_id}>
                      {String(j.job_id).slice(0, 12)}…
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        fontSize: 14, color: 'var(--text-1)', fontWeight: 500,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {cleanJobType(j.job_type)}
                      </div>
                      {j.detail && <div style={SUB_LINE} title={j.detail}>{j.detail}</div>}
                    </div>
                    <div>
                      <StatusBadge status={status} />
                      {ranOnLabel(j.ran_on) && <div style={SUB_LINE}>{ranOnLabel(j.ran_on)}</div>}
                    </div>
                    <div>
                      <SignatureBadge
                        status={j.dropbox_sign_status}
                        testMode={j.dropbox_sign_test_mode}
                      />
                    </div>
                    <div
                      style={{ minWidth: 0, cursor: onOpenProject && proj ? 'pointer' : 'default' }}
                      onClick={(e) => {
                        if (onOpenProject && proj) {
                          e.stopPropagation();
                          onOpenProject(j.project_id);
                        }
                      }}
                      title={[j.project_id, proj?.name].filter(Boolean).join(' — ')}
                    >
                      {/* 'All projects' = a company-wide pull (the agent's label), not a job number */}
                      <div
                        className={j.project_id === 'All projects' ? undefined : 'mono'}
                        style={{
                          fontSize: 13, color: 'var(--text-3)',
                          letterSpacing: j.project_id === 'All projects' ? undefined : '-0.02em',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {j.project_id || '–'}
                      </div>
                      {proj?.name && <div style={{ ...SUB_LINE, color: 'var(--text-2)' }}>{proj.name}</div>}
                    </div>
                    <div style={{
                      fontSize: 14, color: 'var(--text-2)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={j.requested_by}>
                      {j.requested_by || '–'}
                    </div>
                    <div className="mono" style={{
                      fontSize: 13, color: 'var(--text-3)',
                      letterSpacing: '-0.02em',
                      whiteSpace: 'nowrap',
                    }}>
                      {fmtTimestamp(j.started_at || j.requested_at)}
                    </div>
                    <div className="mono" style={{
                      fontSize: 13, color: 'var(--text-3)',
                      letterSpacing: '-0.02em',
                    }}>
                      {fmtDuration(j.duration_seconds)}
                    </div>
                    <div style={{
                      fontSize: 13.5, color: status === 'failed' ? 'var(--danger)' : 'var(--text-3)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={j.error_message || j.message || ''}>
                      {j.error_message || j.message || '–'}
                    </div>
                  </div>
                  {isOpen && (
                    <div style={{
                      background: 'var(--bg-surface-2)',
                      borderBottom: !isLast ? '1px solid var(--border-subtle)' : 'none',
                    }}>
                      <ExpandedDrawer job={j} projectName={proj?.name} onOpenProject={proj ? onOpenProject : null} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────── Expanded drawer ─────────────────────── */

const ExpandedDrawer = ({ job, projectName, onOpenProject }) => {
  const status = cleanStatus(job.status);
  const showFullMessage = job.error_message || job.message;
  return (
    <div style={{ padding: '14px 22px 16px', fontSize: 14 }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'auto 1fr',
        gap: '6px 16px', marginBottom: 14,
      }}>
        <DrawerKV label="Job ID" mono><span title={job.job_id}>{job.job_id}</span></DrawerKV>
        <DrawerKV label="Type">{cleanJobType(job.job_type)}</DrawerKV>
        {job.detail && <DrawerKV label="Report">{job.detail}</DrawerKV>}
        <DrawerKV label="Status"><StatusBadge status={status} /></DrawerKV>
        {job.project_id && (
          <DrawerKV label="Project" mono>
            <span
              style={{
                cursor: onOpenProject ? 'pointer' : 'default',
                color: onOpenProject ? 'var(--accent-text)' : 'var(--text-1)',
              }}
              onClick={() => onOpenProject && onOpenProject(job.project_id)}
            >{job.project_id}</span>
            {projectName && (
              <span style={{ fontFamily: 'var(--font-sans)', color: 'var(--text-2)' }}> · {projectName}</span>
            )}
          </DrawerKV>
        )}
        <DrawerKV label="Requested at">{fmtTimestamp(job.requested_at)}</DrawerKV>
        {job.started_at && (
          <DrawerKV label="Started at">{fmtTimestamp(job.started_at)}</DrawerKV>
        )}
        {job.completed_at && (
          <DrawerKV label="Completed at">{fmtTimestamp(job.completed_at)}</DrawerKV>
        )}
        {job.attempts > 0 && (
          <DrawerKV label="Attempts" mono>{job.attempts}</DrawerKV>
        )}
        {Number(job.retry_count) > 0 && (
          <DrawerKV label="Retries">{job.retry_count} automatic</DrawerKV>
        )}
        {job.error_code && (
          <DrawerKV label="Error code" mono valueColor="var(--danger)">{job.error_code}</DrawerKV>
        )}
        {job.dropbox_sign_status && (
          <DrawerKV label="Signature">
            <SignatureBadge
              status={job.dropbox_sign_status}
              testMode={job.dropbox_sign_test_mode}
            />
          </DrawerKV>
        )}
        {job.dropbox_sign_envelope_id && (
          <DrawerKV label="Envelope" mono>
            <span title={job.dropbox_sign_envelope_id}>{job.dropbox_sign_envelope_id}</span>
          </DrawerKV>
        )}
        {job.dropbox_sign_sent_at && (
          <DrawerKV label="Sent for signature">{fmtTimestamp(job.dropbox_sign_sent_at)}</DrawerKV>
        )}
        {job.dropbox_sign_completed_at && (
          <DrawerKV label="Signature completed">{fmtTimestamp(job.dropbox_sign_completed_at)}</DrawerKV>
        )}
      </div>
      {showFullMessage && (
        <div style={{
          padding: '10px 12px',
          borderRadius: 8,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          color: status === 'failed' ? 'var(--danger)' : 'var(--text-1)',
          fontSize: 13.5, lineHeight: 1.55,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          boxShadow: status === 'failed' ? 'inset 3px 0 0 var(--danger)' : 'none',
        }}>{showFullMessage}</div>
      )}
      {job.replay_of_job_id && (
        <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-3)' }}>
          ↳ This job is a replay of{' '}
          <span className="mono" style={{ color: 'var(--text-1)', letterSpacing: '-0.02em' }}>
            {job.replay_of_job_id}
          </span>
        </div>
      )}
      {(['failed', 'partial', 'parked_waiting'].includes(status) || Number(job.retry_count) > 0) && (
        <ScreenshotsSection jobId={job.job_id} />
      )}
      {job.replays && job.replays.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-3)' }}>
          Replayed {job.replays.length} time{job.replays.length === 1 ? '' : 's'}:{' '}
          {job.replays.map((r, i) => (
            <span key={r.job_id}>
              <span className="mono" style={{ color: 'var(--text-1)', letterSpacing: '-0.02em' }}>
                {r.job_id.slice(0, 12)}…
              </span>
              {' ('}{cleanStatus(r.status)}{')'}
              {i < job.replays.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────── Screenshots (flight recorder) ─────────────────────── */

// "2026-10-11T20:43:02" (the agent's own clock, Central) -> "8:43:02 PM"; falls back to a date.
const shotTime = (s) => {
  const m = /T(\d{2}):(\d{2}):(\d{2})/.exec(s || '');
  if (m) { const h = Number(m[1]); return `${h % 12 || 12}:${m[2]}:${m[3]} ${h < 12 ? 'AM' : 'PM'}`; }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { timeZone: 'America/Chicago' });
};
const shotLabel = (shot, frames) => (shot.kind === 'mark'
  ? `Try ${shot.try} · at the moment it failed (full size) · ${shotTime(shot.captured)}`
  : `Try ${shot.try} · frame ${shot.index + 1} of ${frames} · ${shotTime(shot.captured)}`);

/**
 * The CE agent's flight recorder for this job: the last ~2 minutes of screen of each try and
 * a full-size shot at the moment it failed (CE-Agent flight_recorder.py, kept 90 days). VP /
 * Admin only: anyone else gets 403 and sees nothing. Images are fetched with the sign-in header
 * (fetchCEScreenshotUrl) when a thumbnail scrolls into view, and revoked when the drawer closes.
 */
const ScreenshotsSection = ({ jobId }) => {
  const [shots, setShots] = useState(null);   // null = loading, [] = none saved
  const [state, setState] = useState('loading'); // loading | ready | hidden | error
  const [sel, setSel] = useState(0);
  const [open, setOpen] = useState({});       // try -> show every frame
  const [urls, setUrls] = useState({});       // name -> object URL
  const asked = useRef(new Set());
  const made = useRef([]);
  const viewerRef = useRef(null);

  useEffect(() => {
    let live = true;
    getCEJobScreenshots(jobId).then((r) => {
      if (!live) return;
      const list = (r && r.shots) || [];
      setShots(list);
      setState('ready');
      // Open on the most telling shot: the last try's full-size failure shot, else its last frame.
      setSel(list.length ? list.length - 1 : 0);
    }).catch((e) => {
      if (!live) return;
      setState(e && e.status === 403 ? 'hidden' : 'error');
    });
    return () => {
      live = false;
      made.current.forEach((u) => URL.revokeObjectURL(u));
      made.current = [];
    };
  }, [jobId]);

  const need = useCallback((name) => {
    if (!name || asked.current.has(name)) return;
    asked.current.add(name);
    fetchCEScreenshotUrl(jobId, name).then((u) => {
      made.current.push(u);
      setUrls((prev) => ({ ...prev, [name]: u }));
    }).catch(() => setUrls((prev) => ({ ...prev, [name]: 'error' })));
  }, [jobId]);

  const current = shots && shots[sel];
  useEffect(() => { if (current) need(current.name); }, [current, need]);

  if (state === 'hidden' || (state === 'ready' && !shots.length)) return null;
  if (state === 'loading') {
    return <div style={{ marginTop: 14, fontSize: 13, color: 'var(--text-3)' }}>Looking for screenshots…</div>;
  }
  if (state === 'error') {
    return <div style={{ marginTop: 14, fontSize: 13, color: 'var(--text-3)' }}>Screenshots couldn't be loaded right now.</div>;
  }

  const tries = [...new Set(shots.map((x) => x.try))];
  const framesIn = (t) => shots.filter((x) => x.try === t && x.kind === 'frame').length;
  const step = (d) => setSel((i) => Math.min(shots.length - 1, Math.max(0, i + d)));
  const pick = (i) => { setSel(i); if (viewerRef.current) viewerRef.current.focus(); };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>Screenshots</span>
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>the last 2 minutes of each try · kept 90 days</span>
      </div>
      {tries.map((t) => {
        const own = shots.map((x, i) => ({ ...x, i })).filter((x) => x.try === t);
        const frames = own.filter((x) => x.kind === 'frame');
        const marks = own.filter((x) => x.kind === 'mark');
        const folded = !open[t] && frames.length > 8;
        const shown = folded ? [...frames.slice(0, 5), null, ...frames.slice(-2), ...marks] : [...frames, ...marks];
        const first = own[0];
        const last = own[own.length - 1];
        return (
          <div key={t} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginBottom: 5 }}>
              Try {t} · {shotTime(first.captured)}–{shotTime(last.captured)} · {frames.length} frame{frames.length === 1 ? '' : 's'}
            </div>
            <div style={{ display: 'flex', gap: 5, overflowX: 'auto', paddingBottom: 4 }}>
              {shown.map((x) => (x === null ? (
                <button
                  key="more" type="button" onClick={() => setOpen((o) => ({ ...o, [t]: true }))}
                  style={{ flex: 'none', alignSelf: 'center', padding: '4px 8px', fontSize: 12, color: 'var(--text-2)', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 6, cursor: 'pointer' }}
                >+{frames.length - 7} more</button>
              ) : (
                <Thumb key={x.name} shot={x} url={urls[x.name]} need={need} selected={x.i === sel} onClick={() => pick(x.i)} />
              )))}
            </div>
          </div>
        );
      })}
      {current && (
        <div
          ref={viewerRef} tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
            if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
          }}
          style={{ marginTop: 6, padding: 10, borderRadius: 10, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', outline: 'none' }}
        >
          <div style={{ minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-surface-2)', borderRadius: 6, overflow: 'hidden' }}>
            {urls[current.name] && urls[current.name] !== 'error'
              ? <img src={urls[current.name]} alt={shotLabel(current, framesIn(current.try))} style={{ maxWidth: '100%', maxHeight: 460, display: 'block' }} />
              : <span style={{ fontSize: 13, color: 'var(--text-3)', padding: 40 }}>{urls[current.name] === 'error' ? 'This screenshot could not be loaded.' : 'Loading…'}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 8, fontSize: 13 }}>
            <button type="button" onClick={() => step(-1)} disabled={sel === 0} aria-label="Previous screenshot" style={navBtn(sel === 0)}>◀</button>
            <span style={{ color: current.kind === 'mark' ? 'var(--danger)' : 'var(--text-2)', textAlign: 'center' }}>{shotLabel(current, framesIn(current.try))}</span>
            <span style={{ display: 'flex', gap: 6 }}>
              {urls[current.name] && urls[current.name] !== 'error' && (
                <button type="button" onClick={() => window.open(urls[current.name], '_blank', 'noopener')} style={navBtn(false)}>Open</button>
              )}
              <button type="button" onClick={() => step(1)} disabled={sel === shots.length - 1} aria-label="Next screenshot" style={navBtn(sel === shots.length - 1)}>▶</button>
            </span>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-3)' }}>
            Click a thumbnail to open it here · ← → to step · the red one is the full-size shot at the moment it failed
          </div>
        </div>
      )}
    </div>
  );
};

const navBtn = (disabled) => ({
  padding: '3px 10px', fontSize: 13, borderRadius: 6, cursor: disabled ? 'default' : 'pointer',
  color: disabled ? 'var(--text-4)' : 'var(--text-1)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
});

// A thumbnail fetches its image only once it scrolls into view.
const Thumb = ({ shot, url, need, selected, onClick }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || url) return undefined;
    if (typeof IntersectionObserver === 'undefined') { need(shot.name); return undefined; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { need(shot.name); io.disconnect(); }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [shot.name, url, need]);
  const mark = shot.kind === 'mark';
  return (
    <button
      ref={ref} type="button" onClick={onClick}
      title={mark ? 'At the moment it failed (full size)' : `Frame ${shot.index + 1} · ${shotTime(shot.captured)}`}
      style={{
        flex: 'none', width: 72, height: 45, padding: 0, borderRadius: 4, overflow: 'hidden', cursor: 'pointer',
        background: 'var(--bg-surface-2)',
        border: mark ? '2px solid var(--danger)' : '1px solid var(--border-subtle)',
        outline: selected ? '2px solid var(--accent)' : 'none', outlineOffset: 1,
      }}
    >
      {url && url !== 'error' && <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
    </button>
  );
};

const DrawerKV = ({ label, mono = false, valueColor, children }) => (
  <>
    <span style={{
      fontSize: 12, fontWeight: 600,
      color: 'var(--text-3)',
      textTransform: 'uppercase', letterSpacing: '0.05em',
      whiteSpace: 'nowrap', paddingTop: 2,
    }}>{label}</span>
    <span
      className={mono ? 'mono' : undefined}
      style={{
        color: valueColor || 'var(--text-1)',
        letterSpacing: mono ? '-0.02em' : '-0.005em',
      }}
    >{children}</span>
  </>
);

export default CEActivityPage;
